import React from "react"
import { toast } from "react-toastify"
import Swal from "sweetalert2"
import { IPersonnelDocumentModel } from "@common-models/*"
import {
     CONST_PERSONNEL_DOCUMENT_TYPE_ATTESTATION_SS,
     CONST_PERSONNEL_DOCUMENT_TYPE_AUTRES,
     CONST_PERSONNEL_DOCUMENT_TYPE_CONTRAT,
     CONST_PERSONNEL_DOCUMENT_TYPE_CV,
     CONST_PERSONNEL_DOCUMENT_TYPE_DIPLOME,
     CONST_PERSONNEL_DOCUMENT_TYPE_PASSEPORT_CNI,
     CONST_PERSONNEL_DOCUMENT_TYPE_PERMIS_CONDUIRE,
     CONST_PERSONNEL_DOCUMENT_TYPE_RIB,
     CONST_PERSONNEL_DOCUMENT_TYPE_TITRE_SEJOUR,
     CONST_PERSONNEL_STATUT_STATUT_NON_SALARIE,
} from "@common-constants/*"
import { usePersonnelDetailsContext } from "../PersonnelDetails"
import MyGoogleDriveFileViewerInModal from "../../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import { personnelAddDocumentRequest, personnelDeleteDocumentRequest } from "../../core/_requests"
import FournisseurDocumentsSlots from "../../create/_FournisseurDocumentsSlots"

// Contrat commercial (non salarié) — miroir backend PersonnelDocument::TYPE_CONTRAT_COMMERCIAL.
const DOC_TYPE_CONTRAT_COMMERCIAL = "TYPE_CONTRAT_COMMERCIAL"

type DocConfig = { type: string; label: string; icon: string; multi: boolean }

// Salarié : pièces administratives + contrat de travail.
const SALARIE_CONFIG: DocConfig[] = [
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_CV, label: "Curriculum vitae", icon: "fa-file-lines", multi: false },
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_PASSEPORT_CNI, label: "Passeport ou carte d'identité", icon: "fa-id-card", multi: false },
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_ATTESTATION_SS, label: "Attestation de Sécurité Sociale", icon: "fa-shield-heart", multi: false },
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_RIB, label: "Relevé d'Identité Bancaire (RIB)", icon: "fa-building-columns", multi: false },
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_CONTRAT, label: "Contrat de travail", icon: "fa-file-contract", multi: false },
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_TITRE_SEJOUR, label: "Titre de séjour", icon: "fa-passport", multi: false },
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_PERMIS_CONDUIRE, label: "Permis de conduire", icon: "fa-car", multi: false },
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_DIPLOME, label: "Diplômes", icon: "fa-graduation-cap", multi: true },
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_AUTRES, label: "Autres documents", icon: "fa-folder-open", multi: true },
]

// Non salarié : documents rattachés au collaborateur (les documents société vont sur le fournisseur).
const NON_SALARIE_CONFIG: DocConfig[] = [
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_CV, label: "Curriculum vitae", icon: "fa-file-lines", multi: false },
     { type: DOC_TYPE_CONTRAT_COMMERCIAL, label: "Contrat commercial", icon: "fa-file-signature", multi: false },
     { type: CONST_PERSONNEL_DOCUMENT_TYPE_AUTRES, label: "Autres documents", icon: "fa-folder-open", multi: true },
]

const ACCEPT = "application/pdf, image/*"

// Documents d'un collaborateur intégré : upload des pièces manquantes, remplacement / suppression (soft delete) des pièces présentes.
const PersonnelDocuments = () => {
     const context = usePersonnelDetailsContext()
     const p = context.detailsQuery.data?.personnel
     const personnelId = p?.id as number
     const isNonSalarie = p?.statut === CONST_PERSONNEL_STATUT_STATUT_NON_SALARIE
     const fournisseurId = p?.fournisseur?.id

     const [documents, setDocuments] = React.useState<IPersonnelDocumentModel[]>((p?.personnelDocuments ?? []).filter(d => !d.isDeleted))
     const [busyType, setBusyType] = React.useState<string | null>(null)
     const [dragOverType, setDragOverType] = React.useState<string | null>(null)
     const [activeDoc, setActiveDoc] = React.useState<IPersonnelDocumentModel | null>(null)
     const replaceInputRefs = React.useRef<Record<number, HTMLInputElement | null>>({})

     const config = isNonSalarie ? NON_SALARIE_CONFIG : SALARIE_CONFIG

     // Mise à jour fonctionnelle : plusieurs uploads peuvent être en vol simultanément.
     function update(updater: (prev: IPersonnelDocumentModel[]) => IPersonnelDocumentModel[]) {
          setDocuments(prev => updater(prev))
     }

     async function uploadNew(type: string, file: File) {
          setBusyType(type)
          try {
               const r = await personnelAddDocumentRequest(personnelId, type, file)
               update(prev => [...prev, r.data])
               toast.success("Document ajouté.")
          } catch (e: any) {
               toast.error(e?.response?.data?.detail ?? "Échec de l'envoi du document.", { autoClose: false })
          } finally {
               setBusyType(null)
          }
     }

     async function uploadMany(type: string, files: File[]) {
          setBusyType(type)
          const added: IPersonnelDocumentModel[] = []
          let failures = 0
          for (const file of files) {
               try {
                    const r = await personnelAddDocumentRequest(personnelId, type, file)
                    added.push(r.data)
               } catch {
                    failures++
               }
          }
          if (added.length) update(prev => [...prev, ...added])
          if (added.length) toast.success(`${added.length} document(s) ajouté(s).`)
          if (failures) toast.error(`${failures} document(s) n'ont pas pu être envoyés.`, { autoClose: false })
          setBusyType(null)
     }

     // Remplace : on ajoute le nouveau puis on supprime (soft delete) l'ancien (évite une perte si l'upload échoue).
     async function replace(oldDoc: IPersonnelDocumentModel, type: string, file: File) {
          setBusyType(type)
          try {
               const r = await personnelAddDocumentRequest(personnelId, type, file)
               if (oldDoc.id) await personnelDeleteDocumentRequest(personnelId, oldDoc.id)
               update(prev => [...prev.filter(d => d.id !== oldDoc.id), r.data])
               toast.success("Document remplacé.")
          } catch (e: any) {
               toast.error(e?.response?.data?.detail ?? "Échec du remplacement.", { autoClose: false })
          } finally {
               setBusyType(null)
          }
     }

     function remove(doc: IPersonnelDocumentModel) {
          if (!doc.id) return
          Swal.fire({
               icon: "warning",
               title: "Supprimer ce document ?",
               text: "Le document sera masqué de la fiche (conservé dans la base et sur le Drive).",
               showCancelButton: true,
               confirmButtonText: "Oui, supprimer",
               cancelButtonText: "Annuler",
               confirmButtonColor: "#d33",
               preConfirm: () =>
                    personnelDeleteDocumentRequest(personnelId, doc.id as number)
                         .then(() => update(prev => prev.filter(d => d.id !== doc.id)))
                         .catch(err => Swal.showValidationMessage(err?.response?.data?.detail ?? "Échec de la suppression.")),
          })
     }

     // Ligne d'un document déjà déposé : voir / remplacer / supprimer
     // Fonction appelée (pas un composant) : évite le remontage de l'input file pendant un téléversement concurrent.
     const renderUploadedRow = (doc: IPersonnelDocumentModel, cfg: DocConfig) => (
          <div key={doc.id} className="d-flex align-items-center justify-content-between bg-light-success rounded p-3 mb-2">
               <div className="d-flex align-items-center text-truncate">
                    <i className="fas fa-circle-check text-success fs-4 me-3" />
                    <span className="fw-bold text-truncate" title={doc.nomFichier ?? undefined}>{doc.nomFichier || "Document fourni"}</span>
               </div>
               <div className="d-flex gap-1 flex-shrink-0">
                    <button type="button" className="btn btn-sm btn-icon btn-light-primary" title="Voir" onClick={() => setActiveDoc(doc)}>
                         <i className="fas fa-eye" />
                    </button>
                    <button
                         type="button"
                         className="btn btn-sm btn-icon btn-light-warning"
                         title="Remplacer"
                         disabled={busyType === cfg.type}
                         onClick={() => doc.id && replaceInputRefs.current[doc.id]?.click()}
                    >
                         <i className="fas fa-arrows-rotate" />
                    </button>
                    <button type="button" className="btn btn-sm btn-icon btn-light-danger" title="Supprimer" disabled={busyType === cfg.type} onClick={() => remove(doc)}>
                         <i className="fas fa-trash" />
                    </button>
                    {doc.id && (
                         <input
                              type="file"
                              accept={ACCEPT}
                              className="d-none"
                              ref={el => {
                                   replaceInputRefs.current[doc.id as number] = el
                              }}
                              onChange={e => {
                                   const f = e.target.files?.[0]
                                   if (f) replace(doc, cfg.type, f)
                                   e.target.value = ""
                              }}
                         />
                    )}
               </div>
          </div>
     )

     function handleFiles(cfg: DocConfig, files: File[]) {
          if (!files.length) return
          if (cfg.multi) uploadMany(cfg.type, files)
          else uploadNew(cfg.type, files[0])
     }

     // Zone de dépôt d'un nouveau document — clic OU glisser-déposer, multi-fichiers pour les types « multiple »
     const renderUploadZone = (cfg: DocConfig) => {
          const isDragOver = dragOverType === cfg.type
          return (
               <label
                    className={`d-flex align-items-center justify-content-between rounded p-3 mb-2 cursor-pointer border border-dashed ${isDragOver ? "border-primary bg-light-primary" : "border-gray-300"}`}
                    onDragOver={e => e.preventDefault()}
                    onDragEnter={e => {
                         e.preventDefault()
                         setDragOverType(cfg.type)
                    }}
                    onDragLeave={e => {
                         e.preventDefault()
                         setDragOverType(null)
                    }}
                    onDrop={e => {
                         e.preventDefault()
                         setDragOverType(null)
                         handleFiles(cfg, Array.from(e.dataTransfer.files ?? []))
                    }}
               >
                    <div className="d-flex align-items-center text-muted">
                         <i className={`fas ${isDragOver ? "fa-hand-pointer text-primary" : "fa-circle-plus"} fs-4 me-3`} />
                         <span>
                              {busyType === cfg.type
                                   ? "Envoi en cours…"
                                   : isDragOver
                                   ? "Déposez le fichier ici"
                                   : cfg.multi
                                   ? `Glissez-déposez ou cliquez — un ou plusieurs « ${cfg.label} »`
                                   : `Glissez-déposez ou cliquez — « ${cfg.label} »`}
                         </span>
                    </div>
                    <span className="btn btn-sm btn-light-primary">Parcourir</span>
                    <input
                         type="file"
                         accept={ACCEPT}
                         className="d-none"
                         multiple={cfg.multi}
                         disabled={busyType === cfg.type}
                         onChange={e => {
                              handleFiles(cfg, Array.from(e.target.files ?? []))
                              e.target.value = ""
                         }}
                    />
               </label>
          )
     }

     const renderSlot = (cfg: DocConfig) => {
          const matching = documents.filter(d => d.type === cfg.type)
          return (
               <div className="mb-4" key={cfg.type}>
                    <div className="d-flex align-items-center mb-2">
                         <i className={`fas ${cfg.icon} text-gray-600 me-2`} />
                         <span className="fw-bold">{cfg.label}</span>
                         {cfg.multi && <span className="badge badge-light-secondary ms-2">multiple</span>}
                    </div>
                    {matching.map(doc => renderUploadedRow(doc, cfg))}
                    {/* Slot simple : zone de dépôt si aucun document ; slot multiple : toujours proposer d'ajouter */}
                    {(cfg.multi || matching.length === 0) && renderUploadZone(cfg)}
               </div>
          )
     }

     return (
          <div>
               <div className="fw-bolder fs-5 text-gray-800 mb-4">Documents du collaborateur</div>
               {config.map(renderSlot)}

               {/* Non salarié : documents rattachés au fournisseur (KBIS, RC Pro, Vigilance URSSAF, Régularité fiscale, RIB). */}
               {isNonSalarie && (
                    <>
                         <div className="separator separator-dashed my-6" />
                         <div className="fw-bolder fs-5 text-gray-800 mb-4">Documents du fournisseur</div>
                         {fournisseurId ? (
                              <FournisseurDocumentsSlots fournisseurId={fournisseurId} />
                         ) : (
                              <div className="alert alert-warning mb-0">Aucun fournisseur rattaché à ce collaborateur.</div>
                         )}
                    </>
               )}

               {activeDoc && activeDoc.googleDriveID && (
                    <MyGoogleDriveFileViewerInModal
                         googleDriveID={activeDoc.googleDriveID}
                         googleDriveMimetype={activeDoc.googleDriveMimeType ?? ""}
                         modalTitle={<>{activeDoc.typeLabel ?? "Document"}</>}
                         handleCloseModal={() => setActiveDoc(null)}
                    />
               )}
          </div>
     )
}

export default PersonnelDocuments
